import React, { useState, useEffect } from 'react';
import { askAssistant, AssistantHistoryItem } from '../services/assistantApi';

interface ComplianceRecommendation {
  id: number;
  type: 'FACILITY' | 'PRODUCT' | 'SAFETY' | 'REPORTING' | 'REGULATORY';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  title: string;
  description: string;
  action: string;
  impact: string;
  estimatedTime: string;
  cost: string;
  aiConfidence: number;
}

interface ComplianceQuestion {
  id: number;
  question: string;
  answer: string;
  category: string;
  timestamp: string;
  disclaimer?: string;
  citations?: string[];
  usedAI?: boolean;
}

const AIComplianceAssistant: React.FC = () => {
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<ComplianceRecommendation[]>([]);
  const [chatHistory, setChatHistory] = useState<ComplianceQuestion[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [assistantHistory, setAssistantHistory] = useState<AssistantHistoryItem[]>([]);

  useEffect(() => {
    // Mock AI recommendations - in real app, this would come from AI service
    const mockRecommendations: ComplianceRecommendation[] = [
      {
        id: 1,
        type: 'PRODUCT',
        priority: 'HIGH',
        title: 'Retinol Product Safety Assessment',
        description: 'Your Retinol Serum Pro contains 2% retinol which requires enhanced safety monitoring and specific labeling requirements under MoCRA.',
        action: 'Conduct clinical safety assessment and update product labeling with pregnancy warnings and usage instructions.',
        impact: 'Prevents regulatory violations and potential product recalls',
        estimatedTime: '2-3 weeks',
        cost: '$15,000 - $25,000',
        aiConfidence: 94
      },
      {
        id: 2,
        type: 'FACILITY',
        priority: 'MEDIUM',
        title: 'Facility Registration Renewal',
        description: 'Your main manufacturing facility registration expires in 25 days. Early renewal ensures continuous compliance.',
        action: 'Submit renewal application with updated facility information and compliance documentation.',
        impact: 'Maintains legal manufacturing status',
        estimatedTime: '1 week',
        cost: '$500 - $1,000',
        aiConfidence: 98
      },
      {
        id: 3,
        type: 'REPORTING',
        priority: 'CRITICAL',
        title: 'SAE Reporting Deadline',
        description: 'Serious adverse event for Retinol Serum Pro must be reported to FDA within 15 business days. Current deadline: 3 days remaining.',
        action: 'Immediately submit SAE report with complete clinical documentation and patient information.',
        impact: 'Prevents regulatory penalties and potential legal action',
        estimatedTime: '2-3 days',
        cost: '$5,000 - $10,000',
        aiConfidence: 99
      },
      {
        id: 4,
        type: 'REGULATORY',
        priority: 'HIGH',
        title: 'Small Business Exemption Review',
        description: 'Based on your annual revenue ($2.5M) and employee count (450), you may not qualify for small business exemptions.',
        action: 'Review exemption criteria and prepare for full MoCRA compliance requirements.',
        impact: 'Ensures proper regulatory classification and compliance',
        estimatedTime: '1 week',
        cost: '$2,000 - $5,000',
        aiConfidence: 87
      }
    ];
    setRecommendations(mockRecommendations);
  }, []);

  const handleAskQuestion = async () => {
    if (!userInput.trim()) return;

    setIsLoading(true);
    
    // Simulate AI processing
    try {
      const nextHistory: AssistantHistoryItem[] = [...assistantHistory, { role: 'user', content: userInput }];
      const resp = await askAssistant(userInput, nextHistory);
      const answer = resp?.answer || generateAIResponse(userInput);
      const newQuestion: ComplianceQuestion = {
        id: Date.now(),
        question: userInput,
        answer: answer,
        category: 'MoCRA Compliance',
        timestamp: new Date().toISOString(),
        disclaimer: resp?.disclaimer,
        citations: Array.isArray((resp as any)?.citations) ? (resp as any).citations : undefined,
        usedAI: (resp as any)?.used_ai ?? true
      };
      setAssistantHistory([...nextHistory, { role: 'assistant', content: answer }]);
      setChatHistory(prev => [newQuestion, ...prev]);
      setUserInput('');
    } catch (e) {
      const fallback = generateAIResponse(userInput);
      const newQuestion: ComplianceQuestion = {
        id: Date.now(),
        question: userInput,
        answer: fallback,
        category: 'MoCRA Compliance',
        timestamp: new Date().toISOString(),
        disclaimer: 'Guidance only. Verify with official regulations and your compliance team.',
        usedAI: false
      };
      setAssistantHistory(prev => [...prev, { role: 'user', content: userInput }, { role: 'assistant', content: fallback }]);
      setChatHistory(prev => [newQuestion, ...prev]);
      setUserInput('');
    } finally {
      setIsLoading(false);
    }
  };

  const generateAIResponse = (question: string): string => {
    const lowerQuestion = question.toLowerCase();
    
    if (lowerQuestion.includes('retinol') || lowerQuestion.includes('vitamin a')) {
      return "Retinol products require special attention under MoCRA. Products containing retinol must have enhanced safety monitoring, specific labeling requirements including pregnancy warnings, and may require clinical safety assessments. The FDA considers retinol a high-risk ingredient that requires additional oversight.";
    }
    
    if (lowerQuestion.includes('sae') || lowerQuestion.includes('adverse event')) {
      return "Serious Adverse Events (SAEs) must be reported to the FDA within 15 business days of becoming aware of the event. SAEs include events that result in death, life-threatening situations, hospitalization, significant disability, or congenital anomalies. Your system should automatically flag these events and ensure timely reporting.";
    }
    
    if (lowerQuestion.includes('exemption') || lowerQuestion.includes('small business')) {
      return "Small business exemptions apply to companies with annual revenue under $1 million and fewer than 50 employees. However, even exempt businesses must still comply with basic facility registration and product listing requirements. The exemption primarily affects safety substantiation requirements.";
    }
    
    if (lowerQuestion.includes('facility') || lowerQuestion.includes('registration')) {
      return "All cosmetic manufacturing facilities must register with the FDA and renew registration every two years. This includes facilities that manufacture, package, or distribute cosmetic products. Registration must be completed within 60 days of beginning operations.";
    }
    
    return "I understand your question about MoCRA compliance. To provide the most accurate guidance, I recommend consulting with your compliance team or legal counsel. You can also refer to the FDA's official MoCRA guidance documents for detailed information.";
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'FACILITY': return '🏭';
      case 'PRODUCT': return '🧴';
      case 'SAFETY': return '🛡️';
      case 'REPORTING': return '📊';
      case 'REGULATORY': return '⚖️';
      default: return '📋';
    }
  };

  const filteredRecommendations = selectedCategory === 'all' 
    ? recommendations 
    : recommendations.filter(rec => rec.type === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AI Compliance Assistant</h1>
              <p className="text-gray-600 mt-2">Intelligent MoCRA compliance guidance and recommendations</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* AI Chat Interface */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Ask Compliance Questions</h2>
            
            {/* Chat History */}
            <div className="h-96 overflow-y-auto mb-4 space-y-4">
              {chatHistory.map((chat) => (
                <div key={chat.id} className="space-y-2">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <p className="text-sm text-gray-900 font-medium">Q: {chat.question}</p>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-3 ml-4">
                    <p className="text-sm text-gray-900">{chat.answer}</p>
                    {chat.disclaimer && (
                      <p className="text-xs text-gray-500 mt-1">{chat.disclaimer}</p>
                    )}
                    {Array.isArray(chat.citations) && chat.citations.length > 0 && (
                      <div className="mt-1">
                        <ul className="list-disc ml-4">
                          {chat.citations.slice(0, 3).map((c, i) => (
                            <li key={i} className="text-xs text-gray-600 break-all">{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">{new Date(chat.timestamp).toLocaleString()} • {chat.usedAI ? 'AI' : 'Fallback'}</p>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex items-center space-x-2 text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                  <span className="text-sm">AI is thinking...</span>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="flex space-x-2">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAskQuestion()}
                placeholder="Ask about MoCRA compliance, regulations, deadlines..."
                className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                disabled={isLoading}
              />
              <button
                onClick={handleAskQuestion}
                disabled={isLoading || !userInput.trim()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Ask AI
              </button>
            </div>

            {/* Quick Questions */}
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  "What are retinol requirements?",
                  "SAE reporting deadlines?",
                  "Small business exemptions?",
                  "Facility registration process?"
                ].map((question, index) => (
                  <button
                    key={index}
                    onClick={() => setUserInput(question)}
                    className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* AI Recommendations */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">AI Recommendations</h2>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="all">All Categories</option>
                <option value="FACILITY">Facility</option>
                <option value="PRODUCT">Product</option>
                <option value="SAFETY">Safety</option>
                <option value="REPORTING">Reporting</option>
                <option value="REGULATORY">Regulatory</option>
              </select>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {filteredRecommendations.map((recommendation) => (
                <div key={recommendation.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{getTypeIcon(recommendation.type)}</span>
                      <h3 className="font-medium text-gray-900">{recommendation.title}</h3>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(recommendation.priority)}`}>
                      {recommendation.priority}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{recommendation.description}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Action:</span>
                      <span className="text-gray-900">{recommendation.action}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Impact:</span>
                      <span className="text-gray-900">{recommendation.impact}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Time:</span>
                      <span className="text-gray-900">{recommendation.estimatedTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Cost:</span>
                      <span className="text-gray-900">{recommendation.cost}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">AI Confidence:</span>
                      <span className="text-gray-900">{recommendation.aiConfidence}%</span>
                    </div>
                  </div>

                  <div className="mt-3 flex space-x-2">
                    <button className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700">
                      Implement
                    </button>
                    <button className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                      Dismiss
                    </button>
                    <button className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                      Learn More
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* AI Capabilities */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">AI Capabilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-medium text-gray-900 mb-2">Smart Recommendations</h3>
              <p className="text-sm text-gray-600">AI analyzes your compliance data and provides personalized recommendations</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="font-medium text-gray-900 mb-2">Natural Language Q&A</h3>
              <p className="text-sm text-gray-600">Ask questions in plain English and get instant compliance guidance</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-medium text-gray-900 mb-2">Predictive Analytics</h3>
              <p className="text-sm text-gray-600">Forecast compliance risks and identify potential issues before they occur</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIComplianceAssistant;

